import React, { useState, useEffect, useMemo } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    Stepper,
    Step,
    StepLabel,
    Alert,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    LinearProgress,
    TextField,
    FormHelperText,
    Snackbar,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    alpha,
    Chip,
    Autocomplete,
    FormControlLabel,
    Checkbox,
    FormLabel,
    FormGroup
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useNavigate, useParams } from 'react-router-dom';
import { validateEmail, validatePhone, formatPhoneNumber, validateTime, convertTo24Hour } from '../utils/validators';
import { InputTypes } from '../types/schema';
import { processServerValidations } from '../../../service/api/ValidationService';
import { submitForm } from '../../../service/api/FormSubmissionService';

const RuntimeWizard = ({ wizardSchema, initialData = {}, onDataChange }) => {
    const { wizardId } = useParams();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState(initialData);
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(!wizardSchema);
    const navigate = useNavigate();

    const [serverValidating, setServerValidating] = useState({});
    const [serverErrors, setServerErrors] = useState({});

    const [validatedSections, setValidatedSections] = useState(new Set());
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Update parent when data changes
    useEffect(() => {
        if (onDataChange) {
            const timeoutId = setTimeout(() => {
                onDataChange(formData);
            }, 500);
            return () => clearTimeout(timeoutId);
        }
    }, [formData, onDataChange]);

    useEffect(() => {
        if (wizardSchema) {
            setLoading(false);
        }
    }, [wizardSchema]);

    // Get field from schema
    const getFieldFromSchema = (fieldName) => {
        if (!wizardSchema?.sections) return null;
        for (const section of wizardSchema.sections) {
            const field = section.fields?.find(f => f.name === fieldName);
            if (field) return field;
        }
        return null;
    };

    // Get validation rules from field (handle both old and new schema formats)
    const getFieldValidation = (field) => {
        if (!field) return null;

        // Try new format first
        if (field.customConfig) {
            return {
                ...field.customConfig.validation,
                required: field.customConfig.required || false,
                dataType: field.customConfig.validation?.dataType || 'text'
            };
        }

        // Try old format
        if (field.validation) {
            return field.validation;
        }

        // Default validation
        return {
            required: false,
            minLength: null,
            maxLength: null,
            minValue: null,
            maxValue: null,
            dataType: 'text'
        };
    };

    // Helper function to evaluate a condition
    const evaluateCondition = (condition, sourceValue) => {
        const { operator, triggerValue } = condition;

        switch (operator) {
            case 'equals':
                return sourceValue == triggerValue;
            case 'not_equals':
                return sourceValue != triggerValue;
            case 'contains':
                return String(sourceValue).includes(triggerValue);
            case 'not_contains':
                return !String(sourceValue).includes(triggerValue);
            case 'is_empty':
                return !sourceValue || sourceValue === '' || sourceValue === null || sourceValue === undefined;
            case 'is_not_empty':
                return !!sourceValue && sourceValue !== '' && sourceValue !== null && sourceValue !== undefined;
            default:
                return false;
        }
    };

    const getFieldState = (field, currentSectionIndex) => {
        const defaultState = {
            visible: true,
            disabled: false
        };

        if (!field.conditions || field.conditions.length === 0) {
            return defaultState; // No conditions, always visible and enabled
        }

        let visible = true;
        let disabled = false;

        // All conditions must be true (AND logic)
        field.conditions.forEach(condition => {
            const sourceValue = formData[condition.sourceFieldName];
            const conditionResult = evaluateCondition(condition, sourceValue);

            if (conditionResult) {
                switch (condition.action) {
                    case 'hide':
                        visible = false;
                        break;
                    case 'show':
                        visible = true;
                        break;
                    case 'disable':
                        disabled = true;
                        break;
                    case 'enable':
                        disabled = false;
                        break;
                    default:
                        // Default action
                        break;
                }
            } else {
                // If condition is false, reverse the logic
                switch (condition.action) {
                    case 'hide':
                        visible = true;
                        break;
                    case 'show':
                        visible = false;
                        break;
                    case 'disable':
                        disabled = false;
                        break;
                    case 'enable':
                        disabled = true;
                        break;
                    default:
                        // Default action
                        break;
                }
            }
        });

        return { visible, disabled };
    };

    // New function to check if field is disabled
    const isFieldDisabled = (field, currentSectionIndex) => {
        const state = getFieldState(field, currentSectionIndex);
        return state.disabled;
    };

    // Check if a field should be visible
    const shouldShowField = (field, currentSectionIndex) => {
        if (!field.conditions || field.conditions.length === 0) {
            return true; // No conditions, always show
        }

        // All conditions must be true (AND logic)
        return field.conditions.every(condition => {
            const sourceValue = formData[condition.sourceFieldName];
            const conditionResult = evaluateCondition(condition, sourceValue);

            // If condition is true and action is HIDE, return false (don't show)
            if (conditionResult && condition.action === 'hide') {
                return false;
            }
            // If condition is true and action is SHOW, return true (show)
            if (conditionResult && condition.action === 'show') {
                return true;
            }
            // If condition is false and action is HIDE, return true (show)
            if (!conditionResult && condition.action === 'hide') {
                return true;
            }
            // If condition is false and action is SHOW, return false (don't show)
            if (!conditionResult && condition.action === 'show') {
                return false;
            }

            return true; // Default
        });
    };

    // Check if a section should be skipped
    const shouldSkipSection = (section, sectionIndex) => {
        // First section is never skippable
        if (sectionIndex === 0) return false;

        if (!section.skipRules || section.skipRules.length === 0) {
            return false; // No skip rules, don't skip
        }

        // If ANY skip rule is true, skip the section (OR logic)
        return section.skipRules.some(rule => {
            const sourceValue = formData[rule.sourceFieldName];
            const conditionResult = evaluateCondition(rule, sourceValue);

            return conditionResult && rule.action === 'skip_section';
        });
    };

    // Get visible sections (excluding skipped ones)
    const getVisibleSections = useMemo(() => {
        return wizardSchema.sections.filter((section, index) => {
            // Check if section should be skipped
            return !shouldSkipSection(section, index);
        });
    }, [wizardSchema.sections, formData]);

    // Get current visible section index
    const getCurrentVisibleSectionIndex = () => {
        const visibleSections = getVisibleSections;
        const currentVisibleSection = visibleSections.find((_, idx) => {
            // Find which visible section corresponds to our current activeStep
            // This is complex because we need to map between original indices and visible indices
            return idx === activeStep;
        });

        return wizardSchema.sections.findIndex(s => s.id === currentVisibleSection?.id);
    };


    // Validate a single field
    // Update the validateField function to handle checkbox validation
    const validateField = (fieldName, value, skipHiddenFields = true) => {
        const field = getFieldFromSchema(fieldName);
        if (!field) return null;

        const fieldState = getFieldState(field, activeStep);
        const isVisible = fieldState.visible;
        const isDisabled = fieldState.disabled;

        // Skip validation for hidden fields if skipHiddenFields is true
        if (skipHiddenFields && !isVisible) {
            return null; // Don't validate hidden fields
        }

        // For disabled fields that are visible
        if (isDisabled) {
            return null;
        }

        const validation = getFieldValidation(field);
        const errorMessages = [];

        if (field.type === InputTypes.CHECKBOX) {
            if (validation?.required) {
                if (field.checkboxConfig?.isSingle) {
                    if (value !== true) {
                        errorMessages.push(`${field.label || field.name} must be checked`);
                    }
                } else {
                    const selectedValues = Array.isArray(value) ? value : [];
                    if (selectedValues.length === 0) {
                        errorMessages.push(`Please select at least one option for ${field.label || field.name}`);
                    }
                }
            }

            if (field.checkboxConfig?.isMultiple && Array.isArray(value)) {
                const selectedValues = value;

                if (validation?.minSelections !== null && validation?.minSelections !== undefined) {
                    if (selectedValues.length < validation.minSelections) {
                        errorMessages.push(`Minimum ${validation.minSelections} selection(s) required`);
                    }
                }

                if (validation?.maxSelections !== null && validation?.maxSelections !== undefined) {
                    if (selectedValues.length > validation.maxSelections) {
                        errorMessages.push(`Maximum ${validation.maxSelections} selection(s) allowed`);
                    }
                }
            }

            return errorMessages.length > 0 ? errorMessages[0] : null;
        }

        // Original validation for other field types
        // Required validation
        if (validation?.required && (value === undefined || value === null || value === '' || value === false)) {
            errorMessages.push('This field is required');
            return errorMessages[0];
        }

        // Skip further validation if value is empty and not required
        if (value === undefined || value === null || value === '') {
            return null;
        }

        // Email validation
        if (field.type === 'email') {
            if (!validateEmail(value)) {
                errorMessages.push('Please enter a valid email address');
            }
        }

        // Phone validation
        if (field.type === 'phone') {
            if (!validatePhone(value)) {
                errorMessages.push('Please enter a valid US phone number (e.g., 123-456-7890)');
            }
        }

        // Time validation
        if (field.type === 'time' && typeof value === 'object') {
            if (!validateTime(value)) {
                errorMessages.push('Please select a valid time');
            }
        }

        // String length validation for text fields
        if (field.type === 'textbox' || field.type === 'textarea') {
            const stringValue = value.toString();

            if (validation?.minLength !== null && validation?.minLength !== undefined) {
                if (stringValue.length < validation.minLength) {
                    errorMessages.push(`Minimum ${validation.minLength} characters required`);
                }
            }

            if (validation?.maxLength !== null && validation?.maxLength !== undefined) {
                if (stringValue.length > validation.maxLength) {
                    errorMessages.push(`Maximum ${validation.maxLength} characters allowed`);
                }
            }
        }

        // Numeric validation for textbox with numeric dataType
        if (field.type === 'textbox' && validation?.dataType === 'numeric') {
            const numValue = Number(value);
            if (isNaN(numValue)) {
                errorMessages.push('Please enter a valid number');
            } else {
                if (validation?.minValue !== null && validation?.minValue !== undefined) {
                    if (numValue < validation.minValue) {
                        errorMessages.push(`Minimum value is ${validation.minValue}`);
                    }
                }
                if (validation?.maxValue !== null && validation?.maxValue !== undefined) {
                    if (numValue > validation.maxValue) {
                        errorMessages.push(`Maximum value is ${validation.maxValue}`);
                    }
                }
            }
        }

        // Date validation for datepicker
        if (field.type === 'datepicker') {
            const dateValue = new Date(value);
            if (isNaN(dateValue.getTime())) {
                errorMessages.push('Please enter a valid date');
            }
        }

        // Dropdown/Radio/State validation - ensure value is in options
        if ((field.type === 'dropdown' || field.type === 'radio' || field.type === 'state') && field.options) {
            if (value && !field.options.includes(value)) {
                errorMessages.push('Please select a valid option');
            }
        }

        return errorMessages.length > 0 ? errorMessages[0] : null;
    };

    // Validate current section only (skip hidden fields)
    // Update validateCurrentSection to be async and include server validation
    const validateCurrentSection = async () => {
        const currentSection = wizardSchema.sections[activeStep];
        if (!currentSection) return {};

        // Check if current section should be skipped
        if (shouldSkipSection(currentSection, activeStep)) {
            return {}; // Entire section is skipped, no validation needed
        }

        const newErrors = {};

        // Process all fields sequentially to avoid race conditions
        for (const field of currentSection.fields) {
            if (!field) continue;

            // Check if field is visible
            const fieldState = getFieldState(field, activeStep);
            if (!fieldState.visible) {
                // Remove any existing errors for hidden fields
                if (errors[field.name]) {
                    delete newErrors[field.name];
                }
                continue; // Skip validation for hidden fields
            }

            const value = formData[field.name];

            // 1. Client-side validation (existing)
            const clientError = validateField(field.name, value, true);

            if (clientError) {
                newErrors[field.name] = clientError;
            }

            // 2. Server-side validation (only if no client error and field has server validations)
            if (!clientError && field.serverValidations) {
                const serverError = await validateFieldServerSide(field.name, value);
                if (serverError) {
                    newErrors[field.name] = serverError;
                }
            }
        }

        return newErrors;
    };

    // Get current section errors only
    const currentSectionErrors = useMemo(() => {
        if (!wizardSchema?.sections?.[activeStep]) return {};
        const currentSection = wizardSchema.sections[activeStep];
        const sectionErrors = {};
        Object.keys(errors).forEach(fieldName => {
            const isInCurrentSection = currentSection.fields?.some(field =>
                field && field.name === fieldName
            );
            if (isInCurrentSection) {
                sectionErrors[fieldName] = errors[fieldName];
            }
        });
        return sectionErrors;
    }, [errors, activeStep, wizardSchema]);

    const hasCurrentSectionErrors = useMemo(() => {
        return Object.keys(currentSectionErrors).length > 0;
    }, [currentSectionErrors]);

    const handleNext = async () => {
        try {
            // Show loading state
            setSaving(true);

            // Validate both client and server-side
            const newErrors = await validateCurrentSection();

            // Combine with existing server errors
            const allErrors = { ...newErrors };

            // Also check for any existing server errors in current section
            currentSection.fields.forEach(field => {
                if (field && serverErrors[field.name]) {
                    allErrors[field.name] = serverErrors[field.name];
                }
            });

            console.log('Validation errors:', allErrors);

            if (Object.keys(allErrors).length === 0) {
                // Clear errors for current section
                setErrors(prev => {
                    const updatedErrors = { ...prev };
                    const currentSectionFields = wizardSchema.sections[activeStep].fields.map(f => f.name);

                    // Clear both client and server errors for visible fields
                    currentSectionFields.forEach(fieldName => {
                        const field = getFieldFromSchema(fieldName);
                        const fieldState = getFieldState(field, activeStep);

                        if (fieldState.visible) {
                            delete updatedErrors[fieldName];
                        }
                    });
                    return updatedErrors;
                });

                // Clear server errors for current section
                setServerErrors(prev => {
                    const updatedErrors = { ...prev };
                    const currentSectionFields = wizardSchema.sections[activeStep].fields.map(f => f.name);

                    currentSectionFields.forEach(fieldName => {
                        const field = getFieldFromSchema(fieldName);
                        const fieldState = getFieldState(field, activeStep);

                        if (fieldState.visible) {
                            delete updatedErrors[fieldName];
                        }
                    });
                    return updatedErrors;
                });

                setValidatedSections(prev => new Set([...prev, activeStep]));

                // Find next visible section
                const currentVisibleIndex = getVisibleSections.findIndex(s =>
                    s.id === wizardSchema.sections[activeStep]?.id
                );

                if (currentVisibleIndex < getVisibleSections.length - 1) {
                    const nextVisibleSection = getVisibleSections[currentVisibleIndex + 1];
                    const nextIndex = wizardSchema.sections.findIndex(s => s.id === nextVisibleSection.id);
                    setActiveStep(nextIndex);
                } else {
                    // At last section, validate all visible fields before submit
                    await handleSubmit();
                }
            } else {
                // Set all errors (both client and server)
                setErrors(prev => {
                    const updatedErrors = { ...prev };
                    const currentSectionFields = wizardSchema.sections[activeStep].fields.map(f => f.name);

                    // Clear previous errors for this section
                    currentSectionFields.forEach(fieldName => {
                        delete updatedErrors[fieldName];
                    });

                    // Add new errors
                    Object.keys(allErrors).forEach(fieldName => {
                        if (currentSectionFields.includes(fieldName)) {
                            updatedErrors[fieldName] = allErrors[fieldName];
                        }
                    });

                    return updatedErrors;
                });

                const firstErrorField = Object.keys(allErrors)[0];
                const element = document.getElementById(`field-${firstErrorField}`);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }

                // Show notification
                setNotification({
                    open: true,
                    message: 'Please fix the errors before proceeding',
                    severity: 'error'
                });
            }
        } catch (error) {
            console.error('Error in handleNext:', error);
            setNotification({
                open: true,
                message: 'An error occurred during validation. Please try again.',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        setErrors(prev => {
            const updatedErrors = { ...prev };
            const currentSectionFields = wizardSchema.sections[activeStep].fields.map(f => f.name);

            // Only clear errors for visible fields in current section
            currentSectionFields.forEach(fieldName => {
                const field = getFieldFromSchema(fieldName);
                const fieldState = getFieldState(field, activeStep);

                if (fieldState.visible) {
                    delete updatedErrors[fieldName];
                }
            });
            return updatedErrors;
        });

        // Find previous visible section
        const currentVisibleIndex = getVisibleSections.findIndex(s =>
            s.id === wizardSchema.sections[activeStep]?.id
        );

        if (currentVisibleIndex > 0) {
            const prevVisibleSection = getVisibleSections[currentVisibleIndex - 1];
            const prevIndex = wizardSchema.sections.findIndex(s => s.id === prevVisibleSection.id);
            setActiveStep(prevIndex);
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};
        let hasErrors = false;

        wizardSchema.sections.forEach((section, sectionIndex) => {
            if (shouldSkipSection(section, sectionIndex)) {
                return;
            }

            section.fields?.forEach(field => {
                if (!field) return;

                // Check if field is visible
                const fieldState = getFieldState(field, sectionIndex);
                if (!fieldState.visible) {
                    return; // Skip validation for hidden fields
                }

                const value = formData[field.name];
                const error = validateField(field.name, value, true); // true = skip hidden

                if (error) {
                    newErrors[field.name] = error;
                    hasErrors = true;
                }

                console.log('Server Errors:', serverErrors);
                // Also check for server errors
                if (serverErrors[field.name]) {
                    newErrors[field.name] = serverErrors[field.name];
                    hasErrors = true;
                }
            });
        });

        if (hasErrors) {
            setErrors(newErrors);

            // Find which sections have errors in visible fields
            const sectionsWithErrors = [];
            wizardSchema.sections.forEach((section, sectionIndex) => {
                // Skip validation for skipped sections
                if (shouldSkipSection(section, sectionIndex)) {
                    return;
                }

                const hasErrorsInSection = section.fields?.some(field => {
                    if (!field) return false;
                    const fieldState = getFieldState(field, sectionIndex);
                    return fieldState.visible && newErrors[field.name];
                });

                if (hasErrorsInSection) {
                    sectionsWithErrors.push({
                        index: sectionIndex,
                        title: section.title || `Section ${sectionIndex + 1}`
                    });
                }
            });

            // Navigate to first section with errors
            if (sectionsWithErrors.length > 0) {
                const firstErrorSection = sectionsWithErrors[0];
                if (firstErrorSection.index !== activeStep) {
                    setActiveStep(firstErrorSection.index);
                }

                // Scroll to first error in that section
                const errorSection = wizardSchema.sections[firstErrorSection.index];
                const firstErrorFieldName = errorSection.fields?.find(field =>
                    field && newErrors[field.name]
                )?.name;

                if (firstErrorFieldName) {
                    setTimeout(() => {
                        const element = document.getElementById(`field-${firstErrorFieldName}`);
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }, 100);
                }

                setNotification({
                    open: true,
                    message: `Cannot submit: ${sectionsWithErrors.length} section(s) have errors`,
                    severity: 'error'
                });
            }
            return;
        }

        setSaving(true);

        try {
            const isActivePath = window.location.pathname.startsWith("/forms/active/");
            if (isActivePath) {
                const response = await submitForm(formData, wizardId);
            }
            setSubmitted(true);
            setNotification({
                open: true,
                message: 'Form submitted successfully!',
                severity: 'success'
            });

            if (onDataChange) {
                onDataChange({});
            }
            setValidatedSections(new Set());

        } catch (error) {
            setNotification({
                open: true,
                message: error.message || 'Failed to submit form. Please try again.',
                severity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (fieldName) => (event) => {
        let value;
        const field = getFieldFromSchema(fieldName);

        if (field?.type === 'checkbox' && field.checkboxConfig?.isMultiple) {
            value = event.target.value;
        } else if (field?.type === 'checkbox' && field.checkboxConfig?.isSingle) {
            value = event.target.checked;
        } else if (field?.type === 'radio') {
            value = event.target.value;
        } else if (event.target.type === 'checkbox') {
            value = event.target.checked;
        } else {
            value = event.target.value;
        }

        const newFormData = { ...formData, [fieldName]: value };
        setFormData(newFormData);

        const error = validateField(fieldName, value);
        if (error) {
            setErrors(prev => ({ ...prev, [fieldName]: error }));
        } else if (errors[fieldName]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }

        if (serverErrors[fieldName]) {
            setServerErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleTimeChange = (fieldName, component, value) => {
        const currentTime = formData[fieldName] || {};
        const newTime = { ...currentTime, [component]: value };
        const newFormData = { ...formData, [fieldName]: newTime };
        setFormData(newFormData);

        const error = validateField(fieldName, newTime);
        if (error) {
            setErrors(prev => ({ ...prev, [fieldName]: error }));
        } else if (errors[fieldName]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    // Phone number formatting on blur
    const handlePhoneBlur = (fieldName) => () => {
        const value = formData[fieldName];
        if (value && validatePhone(value)) {
            const formattedPhone = formatPhoneNumber(value);
            setFormData(prev => ({ ...prev, [fieldName]: formattedPhone }));
        }
    };

    // Add this function for server-side validation (separate from existing validation)
    const validateFieldServerSide = async (fieldName, value) => {
        const field = getFieldFromSchema(fieldName);
        if (!field || !field.serverValidations) return null;

        // Skip if value is empty or only whitespace
        if (!value || (typeof value === 'string' && value.trim() === '')) {
            // Clear any existing server error
            if (serverErrors[fieldName]) {
                setServerErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
            }
            return null;
        }

        // Skip if there are already client-side errors
        if (errors[fieldName]) {
            return null;
        }

        setServerValidating(prev => ({ ...prev, [fieldName]: true }));

        try {
            const error = await processServerValidations(field, value, {});

            if (error) {
                setServerErrors(prev => ({ ...prev, [fieldName]: error }));
                return error;
            } else {
                // Clear error if validation passes
                if (serverErrors[fieldName]) {
                    setServerErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[fieldName];
                        return newErrors;
                    });
                }
                return null;
            }
        } catch (error) {
            console.error('Server validation error:', error);
            const errorMsg = 'Server validation error. Please try again.';
            setServerErrors(prev => ({ ...prev, [fieldName]: errorMsg }));
            return errorMsg;
        } finally {
            setServerValidating(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const handleBlur = (fieldName) => () => {
        const value = formData[fieldName];
        const error = validateField(fieldName, value);

        if (error) {
            setErrors(prev => ({ ...prev, [fieldName]: error }));
        }

        // Trigger server validation after a delay
        setTimeout(async () => {
            await validateFieldServerSide(fieldName, value);
        }, 500);

        setSaving(true);
        setTimeout(() => {
            setSaving(false);
        }, 300);
    };

    const renderAlertNotes = (field) => {
        if (!field?.alertNotes || !Array.isArray(field.alertNotes) || field.alertNotes.length === 0) {
            return null;
        }

        const getSeverityIcon = (severity) => {
            switch (severity) {
                case 'info': return <InfoIcon fontSize="inherit" />;
                case 'warning': return <WarningIcon fontSize="inherit" />;
                case 'error': return <ErrorIcon fontSize="inherit" />;
                case 'success': return <CheckCircleIcon fontSize="inherit" />;
                default: return <InfoIcon fontSize="inherit" />;
            }
        };

        return (
            <Box sx={{ mt: 0.5, mb: 2.5 }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    p: 2,
                    bgcolor: 'grey.50',
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: 'divider'
                }}>
                    {field.alertNotes.map((note, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box sx={{
                                flexShrink: 0,
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: `${note.severity || 'info'}.light`,
                                color: `${note.severity || 'info'}.main`,
                                fontSize: '14px'
                            }}>
                                {getSeverityIcon(note.severity)}
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                                {note.text}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        );
    };

    const renderField = (field) => {
        if (!field) return null;

        const isVisible = shouldShowField(field, activeStep);

        if (!isVisible) {
            return null; // Don't render hidden fields
        }

        const validation = getFieldValidation(field);
        const fieldError = errors[field.name];
        const value = formData[field.name] || '';

        const commonTextFieldProps = {
            fullWidth: true,
            margin: 'normal',
            label: field.label || field.name || 'Field',
            value: value,
            onChange: handleChange(field.name),
            onBlur: handleBlur(field.name),
            error: !!fieldError,
            placeholder: field.placeholder,
            id: `field-${field.name}`,
            disabled: submitted,
            required: validation?.required || false,
            helperText: fieldError || ''
        };

        switch (field.type) {
            case 'textbox':
                const inputProps = {};
                if (validation?.dataType === 'numeric') {
                    inputProps.min = validation.minValue;
                    inputProps.max = validation.maxValue;
                    inputProps.step = 'any';
                }

                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <TextField
                            {...commonTextFieldProps}
                            type={validation?.dataType === 'numeric' ? 'number' : 'text'}
                            inputProps={inputProps}
                        />
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'textarea':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <TextField
                            {...commonTextFieldProps}
                            multiline
                            rows={4}
                            inputProps={{
                                minLength: validation?.minLength,
                                maxLength: validation?.maxLength
                            }}
                        />
                        {validation?.minLength && validation?.maxLength && (
                            <FormHelperText>
                                {`Length: ${value?.length || 0} characters (min: ${validation.minLength}, max: ${validation.maxLength})`}
                            </FormHelperText>
                        )}
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'email':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 2.5 }}>
                        <TextField
                            {...commonTextFieldProps}
                            type="email"
                            InputProps={{
                                startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active', opacity: 0.7 }} />
                            }}
                        />
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'phone':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <TextField
                            {...commonTextFieldProps}
                            onBlur={(e) => {
                                handlePhoneBlur(field.name)();
                                handleBlur(field.name)();
                            }}
                            placeholder="(123) 456-7890"
                            InputProps={{
                                startAdornment: <>
                                    <Typography variant="body2" sx={{ mr: 1 }}>+1</Typography>
                                    <PhoneIcon sx={{ mr: 1, color: 'action.active', opacity: 0.7 }} />
                                </>
                            }}
                        />
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'state':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <FormControl fullWidth margin="normal" error={!!fieldError}>
                            <Autocomplete
                                options={field.options || []}
                                value={value || null}
                                onChange={(event, newValue) => {
                                    handleChange(field.name)({
                                        target: { value: newValue || '' }
                                    });
                                }}
                                onBlur={handleBlur(field.name)}
                                disabled={submitted}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={`${field.label || field.name} ${validation?.required ? '*' : ''}`}
                                        error={!!fieldError}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <LocationOnIcon
                                                        sx={{
                                                            mr: 1,
                                                            color: 'action.active',
                                                            opacity: 0.7,
                                                            ml: 1
                                                        }}
                                                    />
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                ListboxProps={{
                                    style: { maxHeight: 300 } // Limit height
                                }}
                                disablePortal={false}
                                size="small"
                            />
                            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                        </FormControl>
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'dropdown':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <FormControl fullWidth margin="normal" error={!!fieldError}>
                            <InputLabel>{field.label || field.name} {validation?.required && '*'}</InputLabel>
                            <Select
                                value={value}
                                onChange={handleChange(field.name)}
                                onBlur={handleBlur(field.name)}
                                label={field.label || field.name}
                                disabled={submitted}
                            >
                                <MenuItem value=""><em>Select an option</em></MenuItem>
                                {field.options?.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                        </FormControl>
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'checkbox':
                if (field.checkboxConfig?.isSingle) {
                    // Single checkbox (boolean) - FIXED
                    // Get the current value - ensure it's a boolean
                    const isChecked = Boolean(value);

                    return (
                        <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                            <FormControl fullWidth margin="normal" error={!!fieldError}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={(e) => {
                                                // Directly pass the boolean value
                                                handleChange(field.name)({
                                                    target: {
                                                        value: e.target.checked,
                                                        type: 'checkbox',
                                                        checked: e.target.checked
                                                    }
                                                });
                                            }}
                                            onBlur={handleBlur(field.name)}
                                            disabled={submitted}
                                        />
                                    }
                                    label={`${field.label || field.name} ${validation?.required ? '*' : ''}`}
                                />
                                {field.description && (
                                    <FormHelperText sx={{ ml: 0 }}>{field.description}</FormHelperText>
                                )}
                                {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                            </FormControl>
                            {renderAlertNotes(field)}
                        </Box>
                    );
                } else {
                    // Checkbox group - WORKS EXACTLY LIKE RADIO BUT WITH MULTIPLE SELECTION
                    const selectedValues = Array.isArray(value) ? value : [];

                    return (
                        <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                            <FormControl
                                fullWidth
                                margin="normal"
                                error={!!fieldError}
                                component="fieldset"
                            >
                                <FormLabel component="legend">
                                    {field.label || field.name} {validation?.required && '*'}
                                </FormLabel>
                                <FormGroup>
                                    {field.options?.map((option, index) => (
                                        <FormControlLabel
                                            key={index}
                                            control={
                                                <Checkbox
                                                    checked={selectedValues.includes(option)}
                                                    onChange={(e) => {
                                                        const newSelected = e.target.checked
                                                            ? [...selectedValues, option]
                                                            : selectedValues.filter(item => item !== option);

                                                        handleChange(field.name)({
                                                            target: {
                                                                value: newSelected,
                                                                type: 'checkbox-group'
                                                            }
                                                        });
                                                    }}
                                                    onBlur={handleBlur(field.name)}
                                                    disabled={submitted}
                                                />
                                            }
                                            label={option}
                                        />
                                    ))}
                                </FormGroup>
                                {field.description && (
                                    <FormHelperText>{field.description}</FormHelperText>
                                )}
                                {fieldError && <FormHelperText>{fieldError}</FormHelperText>}
                            </FormControl>
                            {renderAlertNotes(field)}
                        </Box>
                    );
                }

            case 'radio':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {field.label || field.name}
                                {validation?.required && (
                                    <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                                )}
                            </Typography>
                            {fieldError && <FormHelperText error>{fieldError}</FormHelperText>}
                        </Box>
                        <Box sx={{ ml: 2 }}>
                            {field.options?.map((option, index) => (
                                <Box key={index} display="flex" alignItems="center" mb={1}>
                                    <input
                                        type="radio"
                                        id={`${field.name}-${index}`}
                                        name={field.name}
                                        value={option}
                                        checked={value === option}
                                        onChange={handleChange(field.name)}
                                        onBlur={handleBlur(field.name)}
                                        disabled={submitted}
                                    />
                                    <label
                                        htmlFor={`${field.name}-${index}`}
                                        style={{ marginLeft: '8px', cursor: 'pointer' }}
                                    >
                                        {option}
                                    </label>
                                </Box>
                            ))}
                        </Box>
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'datepicker':
                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <TextField
                            {...commonTextFieldProps}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                        />
                        {renderAlertNotes(field)}
                    </Box>
                );

            case 'time':
                const timeValue = value || {};
                const timeConfig = field.timeConfig || {};
                const timeError = errors[field.name];
                const hasSelectedTime = timeValue.hour && timeValue.minute && timeValue.ampm;

                return (
                    <Box id={`field-container-${field.name}`} sx={{ mb: 0.5 }}>
                        <Paper
                            elevation={1}
                            sx={{
                                p: 3,
                                mt: 2,
                                mb: 2,
                                bgcolor: alpha('#0288d1', 0.03),
                                border: 2,
                                borderColor: timeError ? 'error.light' : 'divider',
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: timeError ? 'error.main' : 'primary.light',
                                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.1)'
                                }
                            }}
                        >
                            {/* Field Header */}
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={2}
                                mb={3}
                                pb={2}
                                borderBottom={1}
                                borderColor="divider"
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: 'primary.main',
                                        color: 'white'
                                    }}
                                >
                                    <AccessTimeIcon fontSize="medium" />
                                </Box>
                                <Box flex={1}>
                                    <Typography variant="h6" fontWeight={600} color="primary.main">
                                        {field.label || field.name}
                                        {validation?.required && (
                                            <span style={{ color: '#d32f2f', marginLeft: '4px' }}>*</span>
                                        )}
                                    </Typography>
                                    {field.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {field.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Time Selection Grid */}
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                {/* Hour */}
                                <Grid item xs={12} sm={timeConfig.includeTimezone ? 3 : 4}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                            color="text.primary"
                                            sx={{ mb: 1, ml: 0.5 }}
                                        >
                                            Hour *
                                        </Typography>
                                        <FormControl fullWidth>
                                            <Select
                                                value={timeValue.hour || ''}
                                                onChange={(e) => handleTimeChange(field.name, 'hour', e.target.value)}
                                                disabled={submitted}
                                                displayEmpty
                                                sx={{
                                                    height: 50,
                                                    borderRadius: 2,
                                                    bgcolor: timeValue.hour ? alpha('#0288d1', 0.05) : 'white',
                                                    border: 2,
                                                    borderColor: timeValue.hour ? 'primary.main' : 'grey.300',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                                                    },
                                                    '& .MuiSelect-select': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        height: '100%',
                                                        paddingLeft: 2,
                                                        fontSize: '1rem',
                                                        fontWeight: timeValue.hour ? 600 : 400
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 300,
                                                            borderRadius: 2,
                                                            marginTop: 1,
                                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem
                                                    value=""
                                                    sx={{
                                                        color: '#999',
                                                        fontSize: '0.95rem',
                                                        minHeight: 48
                                                    }}
                                                >
                                                    <em>Select hour</em>
                                                </MenuItem>
                                                {timeConfig.hourOptions?.map((hour, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={hour}
                                                        sx={{
                                                            minHeight: 48,
                                                            fontSize: '1rem',
                                                            fontWeight: timeValue.hour === hour ? 700 : 500,
                                                            color: timeValue.hour === hour ? 'primary.main' : 'text.primary',
                                                            bgcolor: timeValue.hour === hour ? alpha('#0288d1', 0.08) : 'white',
                                                            '&:hover': {
                                                                bgcolor: timeValue.hour === hour ? alpha('#0288d1', 0.12) : 'grey.50'
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            {hour}
                                                            {timeValue.hour === hour && (
                                                                <Box sx={{ ml: 'auto' }}>
                                                                    <CheckCircleIcon
                                                                        fontSize="small"
                                                                        sx={{ color: 'primary.main' }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>

                                {/* Minute */}
                                <Grid item xs={12} sm={timeConfig.includeTimezone ? 3 : 4}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                            color="text.primary"
                                            sx={{ mb: 1, ml: 0.5 }}
                                        >
                                            Minute *
                                        </Typography>
                                        <FormControl fullWidth>
                                            <Select
                                                value={timeValue.minute || ''}
                                                onChange={(e) => handleTimeChange(field.name, 'minute', e.target.value)}
                                                disabled={submitted}
                                                displayEmpty
                                                sx={{
                                                    height: 50,
                                                    borderRadius: 2,
                                                    bgcolor: timeValue.minute ? alpha('#0288d1', 0.05) : 'white',
                                                    border: 2,
                                                    borderColor: timeValue.minute ? 'primary.main' : 'grey.300',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                                                    },
                                                    '& .MuiSelect-select': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        height: '100%',
                                                        paddingLeft: 2,
                                                        fontSize: '1rem',
                                                        fontWeight: timeValue.minute ? 600 : 400
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 300,
                                                            borderRadius: 2,
                                                            marginTop: 1,
                                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem
                                                    value=""
                                                    sx={{
                                                        color: '#999',
                                                        fontSize: '0.95rem',
                                                        minHeight: 48
                                                    }}
                                                >
                                                    <em>Select minute</em>
                                                </MenuItem>
                                                {timeConfig.minuteOptions?.map((minute, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={minute}
                                                        sx={{
                                                            minHeight: 48,
                                                            fontSize: '1rem',
                                                            fontWeight: timeValue.minute === minute ? 700 : 500,
                                                            color: timeValue.minute === minute ? 'primary.main' : 'text.primary',
                                                            bgcolor: timeValue.minute === minute ? alpha('#0288d1', 0.08) : 'white',
                                                            '&:hover': {
                                                                bgcolor: timeValue.minute === minute ? alpha('#0288d1', 0.12) : 'grey.50'
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            {minute}
                                                            {timeValue.minute === minute && (
                                                                <Box sx={{ ml: 'auto' }}>
                                                                    <CheckCircleIcon
                                                                        fontSize="small"
                                                                        sx={{ color: 'primary.main' }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>

                                {/* AM/PM */}
                                <Grid item xs={12} sm={timeConfig.includeTimezone ? 3 : 4}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Typography
                                            variant="body2"
                                            fontWeight={500}
                                            color="text.primary"
                                            sx={{ mb: 1, ml: 0.5 }}
                                        >
                                            AM/PM *
                                        </Typography>
                                        <FormControl fullWidth>
                                            <Select
                                                value={timeValue.ampm || ''}
                                                onChange={(e) => handleTimeChange(field.name, 'ampm', e.target.value)}
                                                disabled={submitted}
                                                displayEmpty
                                                sx={{
                                                    height: 50,
                                                    borderRadius: 2,
                                                    bgcolor: timeValue.ampm ? alpha('#0288d1', 0.05) : 'white',
                                                    border: 2,
                                                    borderColor: timeValue.ampm ? 'primary.main' : 'grey.300',
                                                    '&:hover': {
                                                        borderColor: 'primary.main',
                                                        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                                                    },
                                                    '& .MuiSelect-select': {
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        height: '100%',
                                                        paddingLeft: 2,
                                                        fontSize: '1rem',
                                                        fontWeight: timeValue.ampm ? 600 : 400
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        border: 'none'
                                                    }
                                                }}
                                                MenuProps={{
                                                    PaperProps: {
                                                        sx: {
                                                            maxHeight: 300,
                                                            borderRadius: 2,
                                                            marginTop: 1,
                                                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                        }
                                                    }
                                                }}
                                            >
                                                <MenuItem
                                                    value=""
                                                    sx={{
                                                        color: '#999',
                                                        fontSize: '0.95rem',
                                                        minHeight: 48
                                                    }}
                                                >
                                                    <em>Select AM/PM</em>
                                                </MenuItem>
                                                {timeConfig.ampmOptions?.map((ampm, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        value={ampm}
                                                        sx={{
                                                            minHeight: 48,
                                                            fontSize: '1rem',
                                                            fontWeight: timeValue.ampm === ampm ? 700 : 500,
                                                            color: timeValue.ampm === ampm ? 'primary.main' : 'text.primary',
                                                            bgcolor: timeValue.ampm === ampm ? alpha('#0288d1', 0.08) : 'white',
                                                            '&:hover': {
                                                                bgcolor: timeValue.ampm === ampm ? alpha('#0288d1', 0.12) : 'grey.50'
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                            {ampm}
                                                            {timeValue.ampm === ampm && (
                                                                <Box sx={{ ml: 'auto' }}>
                                                                    <CheckCircleIcon
                                                                        fontSize="small"
                                                                        sx={{ color: 'primary.main' }}
                                                                    />
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Grid>

                                {/* Timezone - Conditionally rendered */}
                                {timeConfig.includeTimezone && (
                                    <Grid item xs={12} sm={3}>
                                        <Box sx={{ position: 'relative' }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={500}
                                                color="text.primary"
                                                sx={{ mb: 1, ml: 0.5 }}
                                            >
                                                Timezone
                                            </Typography>
                                            <FormControl fullWidth>
                                                <Select
                                                    value={timeValue.timezone || validation?.timezone || ''}
                                                    onChange={(e) => handleTimeChange(field.name, 'timezone', e.target.value)}
                                                    disabled={submitted}
                                                    displayEmpty
                                                    sx={{
                                                        height: 50,
                                                        borderRadius: 2,
                                                        bgcolor: timeValue.timezone ? alpha('#0288d1', 0.05) : 'white',
                                                        border: 2,
                                                        borderColor: timeValue.timezone ? 'primary.main' : 'grey.300',
                                                        '&:hover': {
                                                            borderColor: 'primary.main',
                                                            boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                                                        },
                                                        '& .MuiSelect-select': {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            height: '100%',
                                                            paddingLeft: 2,
                                                            fontSize: '1rem',
                                                            fontWeight: timeValue.timezone ? 600 : 400
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            border: 'none'
                                                        }
                                                    }}
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                maxHeight: 300,
                                                                borderRadius: 2,
                                                                marginTop: 1,
                                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <MenuItem
                                                        value=""
                                                        sx={{
                                                            color: '#999',
                                                            fontSize: '0.95rem',
                                                            minHeight: 48
                                                        }}
                                                    >
                                                        <em>Select timezone</em>
                                                    </MenuItem>
                                                    {timeConfig.availableTimezones?.map((tz, index) => (
                                                        <MenuItem
                                                            key={index}
                                                            value={tz}
                                                            sx={{
                                                                minHeight: 48,
                                                                fontSize: '1rem',
                                                                fontWeight: timeValue.timezone === tz ? 700 : 500,
                                                                color: timeValue.timezone === tz ? 'primary.main' : 'text.primary',
                                                                bgcolor: timeValue.timezone === tz ? alpha('#0288d1', 0.08) : 'white',
                                                                '&:hover': {
                                                                    bgcolor: timeValue.timezone === tz ? alpha('#0288d1', 0.12) : 'grey.50'
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                                {tz}
                                                                {timeValue.timezone === tz && (
                                                                    <Box sx={{ ml: 'auto' }}>
                                                                        <CheckCircleIcon
                                                                            fontSize="small"
                                                                            sx={{ color: 'primary.main' }}
                                                                        />
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Grid>
                                )}
                            </Grid>

                            {/* Selected Time Preview */}
                            {hasSelectedTime && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2.5,
                                        bgcolor: alpha('#0288d1', 0.08),
                                        borderRadius: 2,
                                        border: 1,
                                        borderColor: 'primary.light',
                                        animation: 'fadeIn 0.5s ease'
                                    }}
                                >
                                    <Box display="flex" alignItems="center" justifyContent="space-between">
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '50%',
                                                    bgcolor: 'primary.main',
                                                    color: 'white'
                                                }}
                                            >
                                                <AccessTimeIcon fontSize="small" />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    Selected Time
                                                </Typography>
                                                <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                                                    <Chip
                                                        label={`${timeValue.hour}:${timeValue.minute} ${timeValue.ampm}`}
                                                        color="primary"
                                                        variant="filled"
                                                        size="medium"
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: '0.95rem',
                                                            height: 32
                                                        }}
                                                    />
                                                    {timeValue.timezone && (
                                                        <Chip
                                                            label={timeValue.timezone}
                                                            variant="outlined"
                                                            size="medium"
                                                            sx={{
                                                                borderColor: 'primary.main',
                                                                color: 'primary.main',
                                                                fontWeight: 500,
                                                                fontSize: '0.95rem',
                                                                height: 32
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </Box>
                                        <Box textAlign="right">
                                            <Typography variant="caption" color="text.secondary">
                                                24-hour format
                                            </Typography>
                                            <Typography variant="body1" fontWeight={600}>
                                                {convertTo24Hour(timeValue.hour, timeValue.ampm)}:{timeValue.minute}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Helper Text */}
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <InfoIcon fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    {timeConfig.includeTimezone
                                        ? 'Select hour, minute, AM/PM, and timezone'
                                        : 'Select hour, minute, and AM/PM'}
                                </Typography>
                            </Box>

                            {/* Error Message */}
                            {timeError && (
                                <Alert
                                    severity="error"
                                    icon={<ErrorIcon />}
                                    sx={{
                                        mt: 2,
                                        borderRadius: 2,
                                        alignItems: 'center',
                                        py: 1
                                    }}
                                >
                                    <Typography variant="body2">
                                        {timeError}
                                    </Typography>
                                </Alert>
                            )}
                        </Paper>
                        {renderAlertNotes(field)}
                    </Box>
                );

            default:
                return (
                    <Alert severity="warning" sx={{ mb: 0.5 }}>
                        Unknown field type: {field.type}
                    </Alert>
                );
        }
    };

    // Calculate validation stats
    const totalFields = wizardSchema?.sections?.reduce((total, section) =>
        total + (section.fields?.length || 0), 0
    ) || 0;

    const validFields = Object.keys(formData).filter(key =>
        formData[key] && !errors[key]
    ).length;

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }}>Loading wizard...</Typography>
            </Container>
        );
    }

    if (!wizardSchema || !wizardSchema.sections || !Array.isArray(wizardSchema.sections)) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    Invalid wizard configuration. The wizard schema is missing or corrupted.
                </Alert>
                <Button variant="outlined" onClick={() => navigate('/admin')}>
                    Go to Admin Portal
                </Button>
            </Container>
        );
    }

    if (submitted) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h5" gutterBottom color="success.main">
                        ✓ Form Submitted Successfully!
                    </Typography>
                    <Typography color="text.secondary" paragraph>
                        Thank you for completing the form.
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setSubmitted(false);
                            setFormData({});
                            setActiveStep(0);
                            setErrors({});
                            setValidatedSections(new Set());
                        }}
                        sx={{ mt: 3 }}
                    >
                        Start New Form
                    </Button>
                </Paper>
            </Container>
        );
    }

    const currentSection = wizardSchema.sections[activeStep];

    if (!currentSection) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error">
                    Invalid section index. The wizard configuration may be corrupted.
                </Alert>
                <Button onClick={() => setActiveStep(0)} sx={{ mt: 2 }}>
                    Go to First Section
                </Button>
            </Container>
        );
    }

    return (
        <>
            <Box>
                {saving && (
                    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
                        <LinearProgress />
                    </Box>
                )}

                <Paper sx={{ p: 4 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" gutterBottom>
                            {wizardSchema.name || 'Form Wizard'}
                        </Typography>
                        {wizardSchema.description && (
                            <Typography color="text.secondary" paragraph>
                                {wizardSchema.description}
                            </Typography>
                        )}
                    </Box>

                    <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                        {getVisibleSections.map((section, index) => {
                            const originalIndex = wizardSchema.sections.findIndex(s => s.id === section.id);
                            const isCurrentStep = originalIndex === activeStep;
                            const hasErrors = section.fields?.some(field => {
                                if (!field) return false;
                                const fieldState = getFieldState(field, originalIndex);
                                return fieldState.visible && errors[field.name];
                            });

                            return (
                                <Step key={section?.id || index}>
                                    <StepLabel
                                        error={hasErrors}
                                        completed={validatedSections.has(originalIndex) && !hasErrors}
                                    >
                                        {section?.title || `Section ${index + 1}`}
                                        {validatedSections.has(originalIndex) && !hasErrors && (
                                            <span style={{ fontSize: '0.75rem', color: 'green', marginLeft: '8px' }}>
                                                ✓
                                            </span>
                                        )}
                                        {hasErrors && (
                                            <span style={{ fontSize: '0.75rem', color: 'red', marginLeft: '8px' }}>
                                                ✗
                                            </span>
                                        )}
                                    </StepLabel>
                                </Step>
                            );
                        })}
                    </Stepper>

                    <Typography variant="h6" gutterBottom>
                        {currentSection.title || `Section ${activeStep + 1}`}
                    </Typography>

                    {currentSection.description && (
                        <Typography color="text.secondary" paragraph>
                            {currentSection.description}
                        </Typography>
                    )}

                    {/* Validation Summary */}
                    {Object.keys(currentSectionErrors).length > 0 && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            <Typography variant="subtitle2">
                                Please fix the following errors in this section before proceeding:
                            </Typography>
                            <List dense>
                                {Object.entries(currentSectionErrors).map(([fieldName, error]) => {
                                    const field = getFieldFromSchema(fieldName);
                                    return (
                                        <ListItem key={fieldName} sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 32 }}>
                                                <ErrorIcon fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`${field?.label || fieldName}: ${error}`}
                                                primaryTypographyProps={{ variant: 'body2' }}
                                            />
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Alert>
                    )}

                    <Box component="form" sx={{ mt: 3 }}>
                        {currentSection.fields?.map((field, index) => (
                            <Box key={field?.id || index}>
                                {renderField(field)}
                            </Box>
                        ))}
                    </Box>

                    <Box display="flex" justifyContent="space-between" mt={4}>
                        <Button
                            disabled={activeStep === 0}
                            onClick={handleBack}
                            variant="outlined"
                        >
                            Back
                        </Button>

                        <Box display="flex" alignItems="center" gap={2}>
                            {saving && (
                                <Box display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={20} />
                                    <Typography variant="caption" color="text.secondary">
                                        Auto-saving...
                                    </Typography>
                                </Box>
                            )}

                            {activeStep === wizardSchema.sections.length - 1 ? (
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={saving}
                                    color="success"
                                >
                                    Submit Form
                                </Button>
                            ) : (
                                <Button
                                    variant="contained"
                                    onClick={handleNext}
                                    disabled={saving || hasCurrentSectionErrors}
                                >
                                    Next
                                </Button>
                            )}
                        </Box>
                    </Box>

                    {/* Progress indicator */}
                    <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" color="text.secondary">
                                Progress: {Math.round(((activeStep + 1) / wizardSchema.sections.length) * 100)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Fields: {validFields}/{totalFields} completed
                            </Typography>
                        </Box>
                        <Box sx={{ width: '100%', height: 4, bgcolor: 'grey.200', borderRadius: 2, mt: 1 }}>
                            <Box
                                sx={{
                                    width: `${((activeStep + 1) / wizardSchema.sections.length) * 100}%`,
                                    height: '100%',
                                    bgcolor: 'primary.main',
                                    borderRadius: 2
                                }}
                            />
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={notification.severity}
                    elevation={6}
                    variant="filled"
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default RuntimeWizard;